"use client";

import React from "react";
import { Activity, ArrowRight, Clock, PhoneCall, ShieldCheck, ThermometerSnowflake } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function CompleteHomeComfort() {
  const {
    ref: textRef,
    isVisible: isTextVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: cardRef,
    isVisible: isCardVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="138779" id="the-circuit-board" className="dark bg-background text-foreground relative min-h-[90vh] flex items-center overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        {}
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/thermal_imaging_hvac_system.png)] bg-cover bg-center opacity-10 mix-blend-luminosity saturate-50"></div>
        
        {}
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/modern_ac_unit_outdoor.png)] bg-cover bg-center opacity-30 [mask-image:linear-gradient(to_top,transparent,black)] md:[mask-image:linear-gradient(to_right,transparent_20%,black_80%)]"></div>
        
        {}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/90 md:via-background/80 to-background/30 md:bg-gradient-to-r md:from-background md:via-background/85 md:to-background/10"></div>
        
        {}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[140px] mix-blend-overlay"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div ref={textRef} className={`lg:col-span-7 xl:col-span-6 flex flex-col items-start space-y-8 transition-all duration-1000 ease-out ${isTextVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/80 border border-border/40 backdrop-blur-md">
              <span className="relative flex size-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-destructive opacity-75"></span>
                <span className="relative inline-flex rounded-full size-2.5 bg-destructive"></span>
              </span>
              <span className="text-xs font-mono font-semibold tracking-widest text-secondary-foreground uppercase">
                Latest Electrical Safety Insights
              </span>
            </div>

            <div className="space-y-4">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold tracking-tighter text-foreground leading-[1.05]">
                Mastering <br className="hidden md:block" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-primary/60">
                  The Circuit Board
                </span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-2xl leading-relaxed font-sans">
                Your essential guide to electrical safety, generator maintenance, and home upgrades. Expert advice from Humberg Electrical to keep your Texas home powered and protected.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto pt-4">
              <Button size="lg" className="h-14 px-8 text-base font-bold rounded-sm shadow-xl shadow-primary/10 group bg-destructive hover:bg-destructive/90 text-destructive-foreground" asChild>
                <Link to="/BlogPage">
                  <ThermometerSnowflake className="mr-2 size-5 group-hover:rotate-12 transition-transform" />
                  Read Blog
                  <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base font-bold rounded-sm bg-background/10 backdrop-blur-sm border-primary/50 text-primary hover:bg-primary/10" asChild>
                <Link to="/ContactPage">
                  <PhoneCall className="mr-2 size-5" />
                  Ask a Pro
                </Link>
              </Button>
            </div>

            <div className="flex items-center gap-6 pt-6 border-t border-border/20 w-full max-w-md">
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono uppercase tracking-tight">
                <ShieldCheck className="size-5 text-destructive" />
                Safety First
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono uppercase tracking-tight">
                <Clock className="size-5 text-primary" />
                Pro Maintenance
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 xl:col-span-5 xl:col-start-8 relative">
            <div ref={cardRef} className={`relative transition-all duration-1000 delay-300 ease-out ${isCardVisible ? "opacity-100 translate-y-0 lg:translate-x-0" : "opacity-0 translate-y-12 lg:translate-y-0 lg:translate-x-12"}`}>
              {}
              <div className="absolute -inset-1 bg-gradient-to-br from-primary/40 to-transparent rounded-sm blur-2xl opacity-40"></div>
              
              <Card className="relative bg-card/60 backdrop-blur-2xl border-border/40 shadow-2xl shadow-black/60 overflow-hidden rounded-sm">
                <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-destructive via-primary to-transparent"></div>
                
                <CardHeader className="pb-4">
                  <CardDescription className="flex items-center gap-2 text-primary uppercase tracking-widest text-[10px] font-mono font-bold">
                    <Activity className="size-4 animate-pulse" />
                    Trending Topic
                  </CardDescription>
                  <CardTitle className="text-3xl md:text-4xl font-sans font-extrabold tracking-tight text-foreground mt-2">
                    Panel <span className="text-lg md:text-xl text-primary font-mono block mt-1">Upgrades & Safety</span>
                  </CardTitle>
                </CardHeader>
                
                <CardContent className="space-y-6">
                  <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                    Discover why upgrading your electrical panel is the most critical safety improvement for modern Texas homes and high-capacity appliances.
                  </p>
                  
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-[11px] font-mono uppercase tracking-wider">
                      <span className="text-muted-foreground">Expert Rating</span>
                      <span className="font-bold text-foreground">4.9/5 Trust Score</span>
                    </div>
                    <div className="w-full bg-muted/40 rounded-none h-1 overflow-hidden">
                      <div className="bg-primary h-full w-[94%] relative">
                        <div className="absolute top-0 right-0 bottom-0 w-12 bg-gradient-to-r from-transparent to-white/20 animate-[shimmer_3s_infinite]"></div>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex flex-wrap gap-2">
                    <Badge variant="secondary" className="bg-secondary/40 backdrop-blur-sm border-border/20 rounded-sm font-mono text-[10px] uppercase">Generators</Badge>
                    <Badge variant="secondary" className="bg-secondary/40 backdrop-blur-sm border-border/20 rounded-sm font-mono text-[10px] uppercase">EV Charging</Badge>
                    <Badge variant="secondary" className="bg-secondary/40 backdrop-blur-sm border-border/20 rounded-sm font-mono text-[10px] uppercase">Inspections</Badge>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>

        </div>
      </div>
    </section>;
}